import { updateEntryAnnotations } from "@modules/bookmarks/entries/entries-api"
import { InfoPanel } from "@modules/info-panel/InfoPanel"
import type { JQuery } from "@modules/jquery-types"
import { addEventListenerAll } from "@modules/util/events"

type SuccessCallback = (rating: number, notes: string) => Promise<void> | void

export type NotesRatingModalData = {
    userId: number
    canonicalId: string
    advertId: number
    rating: number
    notes: string
    successCallback?: SuccessCallback
}

type ComponentMap = {
    notes: HTMLTextAreaElement
    ratedValue: HTMLInputElement
    rating: HTMLElement[]
    removeLink: HTMLAnchorElement[]
    removeButton: HTMLButtonElement
    saveButton: HTMLButtonElement
}

type ComponentKey = keyof ComponentMap

const ModalComponents: { [K in keyof ComponentMap]: K } = {
    notes: "notes",
    ratedValue: "ratedValue",
    rating: "rating",
    removeLink: "removeLink",
    removeButton: "removeButton",
    saveButton: "saveButton",
}

const starClickAnimation = (element: HTMLElement): Animation =>
    new Animation(
        new KeyframeEffect(
            element,
            [
                {
                    opacity: 1,
                    transform: "translate(-50%, -50%) scale3d(0.3,0.3,1)",
                },
                {
                    opacity: 0.1,
                    boxShadow: "inset 0 0 0 2px rgba(226,140,19,0.8)",
                    offset: 0.8,
                },
                {
                    opacity: 0,
                    transform: "translate(-50%, -50%) scale3d(0.9,0.9,1)",
                    boxShadow: "inset 0 0 0 2px rgba(226,140,19,0.8)",
                },
            ],
            {
                duration: 400,
                easing: "ease-out",
                pseudoElement: "::after",
            },
        ),
    )

namespace Modal {
    export class NotesRatingModal {
        private readonly annotationsContainerElement: HTMLElement
        private readonly annotationsRemoveContainerElement: HTMLElement
        private readonly components: ComponentMap
        private userId = 0
        private canonicalId = ""
        private advertId = 0
        private originalState = {
            rating: 0,
            notes: "",
        }
        private successCallback: SuccessCallback = (): void => {}

        private readonly ratingClickHandler = (clickEvent: Event): void => {
            const icon = clickEvent.target as HTMLAnchorElement
            const ratingString = icon.dataset.value ?? "0"
            const animation: Animation = starClickAnimation(icon)

            animation.play()

            this.components.ratedValue.value = ratingString
            this.updateStarsIcons(ratingString)
        }

        /** @throws Error */
        constructor() {
            const annotationsContainer: HTMLElement | null = document.getElementById("notes-rating-modal")
            const annotationsRemoveContainerElement: HTMLElement | null =
                document.getElementById("clear-annotations-modal")

            if (!annotationsContainer) {
                throw new Error("Notes & rating modal container not found")
            }

            if (!annotationsRemoveContainerElement) {
                throw new Error("Notes & rating removal modal container not found")
            }

            this.annotationsContainerElement = annotationsContainer
            this.annotationsRemoveContainerElement = annotationsRemoveContainerElement
            this.components = this.collectComponents()

            this.components.saveButton.addEventListener("click", (): Promise<void> => this.saveModalData(), {
                passive: true,
            })
            addEventListenerAll(this.components.removeLink, "click", (clickEvent: PointerEvent): void => {
                clickEvent.preventDefault()

                this.clearModal<JQuery>($).modal("show")
            })
            this.components.removeButton.addEventListener("click", (): Promise<void> => this.clearModalData(), {
                passive: true,
            })
        }

        public modal<T = HTMLElement>(wrapperCallback?: (element: HTMLElement) => T): T {
            if (!wrapperCallback) {
                return this.annotationsContainerElement as T
            }

            return wrapperCallback(this.annotationsContainerElement)
        }

        public clearModal<T = HTMLElement>(wrapperCallback?: (element: HTMLElement) => T): T {
            if (!wrapperCallback) {
                return this.annotationsRemoveContainerElement as T
            }

            return wrapperCallback(this.annotationsRemoveContainerElement)
        }

        public updateModalData(data: NotesRatingModalData): void {
            const ratingString = data.rating.toString()

            this.userId = data.userId
            this.canonicalId = data.canonicalId
            this.advertId = data.advertId

            this.components.ratedValue.value = ratingString
            this.originalState.rating = data.rating

            this.components.notes.textContent = data.notes
            this.originalState.notes = data.notes

            this.updateStarsIcons(ratingString)

            for (const removeLinkElement of this.components.removeLink) {
                removeLinkElement.classList.add("hidden")

                if (data.rating > 0 || data.notes !== "") {
                    removeLinkElement.classList.remove("hidden")
                }
            }

            this.components.notes.dispatchEvent(new Event("input"))

            if (data.successCallback !== undefined) {
                this.successCallback = data.successCallback
            }
        }

        private updateStarsIcons(ratingString: string) {
            for (const ratingIcon of this.components.rating) {
                ratingIcon.classList.remove("active")

                if (ratingIcon.dataset.value === ratingString) {
                    ratingIcon.classList.add("active")
                }
            }
        }

        /** @throws Error */
        private collectComponents(): ComponentMap {
            const elements: HTMLElement[] = [
                ...this.annotationsContainerElement.querySelectorAll<HTMLElement>("[data-component]"),
                ...this.annotationsRemoveContainerElement.querySelectorAll<HTMLElement>("[data-component]"),
            ]

            const map = {} as Partial<ComponentMap>

            for (const element of elements) {
                const name = element.dataset.component as ComponentKey | undefined

                if (!name) {
                    continue
                }

                switch (name) {
                    case ModalComponents.removeButton:
                    case ModalComponents.saveButton:
                        map[name] = element as HTMLButtonElement
                        break

                    case ModalComponents.removeLink:
                        if (!map[name]) {
                            map[name] = []
                        }
                        map[name].push(element as HTMLAnchorElement)
                        break

                    case ModalComponents.notes:
                        map[name] = element as HTMLTextAreaElement
                        break

                    case ModalComponents.ratedValue:
                        map[name] = element as HTMLInputElement
                        break

                    case ModalComponents.rating:
                        if (!map[name]) {
                            map[name] = []
                        }
                        element.removeEventListener("click", this.ratingClickHandler)
                        element.addEventListener("click", this.ratingClickHandler, { passive: true })
                        map[name].push(element)
                        break

                    default:
                        console.warn(`Unknown component: ${name}`)
                }
            }

            this.ensureRequiredComponentsExist(map)

            return map as ComponentMap
        }

        /** @throws Error */
        private ensureRequiredComponentsExist(map: Partial<ComponentMap>): void {
            const requiredKeys: ComponentKey[] = Object.values(ModalComponents)

            for (const key of requiredKeys) {
                if (!map[key]) {
                    throw new Error(`Missing required modal component: ${key}`)
                }
            }
        }

        private async clearModalData(): Promise<void> {
            this.components.ratedValue.value = "0"
            this.components.notes.value = ""

            this.clearModal<JQuery>($).modal("hide")

            await this.saveModalData()
        }

        private async saveModalData(): Promise<void> {
            const newRating: number = Number.parseFloat(this.components.ratedValue.value)
            const newNotes: string = this.components.notes.value

            if (newRating === this.originalState.rating && newNotes === this.originalState.notes) {
                this.modal<JQuery>($).modal("hide")
                return
            }

            const success: boolean = await updateEntryAnnotations(
                this.userId,
                this.canonicalId,
                this.advertId,
                newRating,
                newNotes,
            )

            if (success) {
                this.successCallback(newRating, newNotes)
                InfoPanel.displayMessage("Die Notiz/Bewertung wurde erfolgreich gespeichert.")
                this.modal<JQuery>($).modal("hide")
                return
            }

            InfoPanel.displayMessage("Nope")
        }
    }
}

export const NotesRatingModal = new Modal.NotesRatingModal()
